import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Button,
} from '@mui/material';
import { memoryService } from '../../services/memoryService';
import { useNotificationStore } from '../../store/notificationStore';
import { MemoryType, type AgentMemoryEntryDto } from '../../types/memory';

const typeLabel = (type: number) => {
  switch (type) {
    case MemoryType.Fact:
      return 'Fact';
    case MemoryType.Decision:
      return 'Decision';
    case MemoryType.Outcome:
      return 'Outcome';
    case MemoryType.Preference:
      return 'Preference';
    default:
      return 'Unknown';
  }
};

/**
 * Compact memory list component for a session sidebar.
 */
export const SessionMemoryList = ({ sessionId }: { sessionId: string }) => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [memories, setMemories] = useState<AgentMemoryEntryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const result = await memoryService.getSessionMemories(sessionId, {
        query: query || undefined,
        useSemanticSearch: true,
        limit: 10,
      });
      setMemories(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load memories';
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, query]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle2">Memories</Typography>
        <Button size="small" onClick={() => navigate(`/memories?sessionId=${sessionId}`)}>
          View all
        </Button>
      </Box>

      <TextField
        size="small"
        placeholder="Search memories..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mt: 1, mb: 1 }}
        fullWidth
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <List dense disablePadding>
          {memories.length === 0 ? (
            <Box sx={{ px: 1, py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                No memories yet.
              </Typography>
            </Box>
          ) : (
            memories.map((m) => (
              <ListItemButton key={m.id} onClick={() => navigate(`/memories/${m.id}`)}>
                <ListItemText
                  primary={m.summary || (m.content.length > 60 ? `${m.content.slice(0, 60)}…` : m.content)}
                  secondary={
                    <span>
                      <Chip
                        label={typeLabel(m.type)}
                        size="small"
                        sx={{ mr: 0.5 }}
                        variant="outlined"
                      />
                      <span>{m.importanceScore.toFixed(2)}</span>
                    </span>
                  }
                />
              </ListItemButton>
            ))
          )}
        </List>
      )}
    </Box>
  );
};

